import React, { useState, useRef, useEffect } from "react";
import { utils, write } from "xlsx";
import moment from "moment";
import Papa from "papaparse";
import { Menu } from 'primereact/menu';
import { useDispatch, useSelector } from "react-redux";

import { DropDownField, TextInputField, MultiSelectField, AutoCompleteField } from "../../../helpers";
import Button from "../../../helpers/Button";
import DateFilter from "../../../common/DateFilter";

import { fetchSellerPOVendorPayments, updateSearchOptions, updateSearchParams, setIsSearchByLocked } from "../../../services/redux/reducers/vendorPayment/vendorPayment";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { searchSellerPOVendorPayments } from "../../../services/api/vendorPayment";
import { getAddFilterOptions, getAutoSearchSuggestions, getDateRange, getFullName, getMatchedRegexPattern, normalizeLabel, round, useFuzzSearch } from "../../../utils";
import { fetchBuyerCompanies } from "../../../services/redux/reducers/commonThunk";

import { DATE_FORMAT, SELLERPO_VP_MIN_PRICE_DIFF } from "../../../constants";

const docNoSearchLabels = ["docNo", "vendorPaymentDocNo", "sellerPINumber", "sellerPIDocNo", "sellerPODocNo", "pettyCashDocNo", "chequeNo"]


const Search = (props) => {
    const { expenseCategories } = props

    const [suggestions, setSuggestions] = useState([])

    const isTableLoading = useSelector(state => state.vendorPaymentsSellerPO.loading)
    const usersData = useSelector(state => state.userDetails)
    const userDetails = useSelector(state => state.userDetails)
    const searchParams = useSelector(state => state.vendorPaymentsSellerPO.searchParams)
    const paginationParams = useSelector(state => state.vendorPaymentsSellerPO.paginationParams)
    const searchOptions = useSelector(state => state.vendorPaymentsSellerPO.searchOptions)
    const searchConfig = useSelector(state => state.vendorPaymentsSellerPO.searchConfig)
    const isSearchByLocked = useSelector(state => state.vendorPaymentsSellerPO.isSearchByLocked)

    const companyBranches = usersData.PMCompanyDetails.companyBranches

    const dispatch = useDispatch()

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    let pmCompanyUsers = userDetails.PMCompanyDetails.companyUsers ? userDetails.PMCompanyDetails.companyUsers : []
    let buyerCompanies = useSelector(state => state.common.buyerCompanies)

    pmCompanyUsers = pmCompanyUsers.map(eachPerson => {
        const name = getFullName(eachPerson.firstName, eachPerson.lastName)
        return { ...eachPerson, displayName: name }
    })

    if (buyerCompanies && buyerCompanies.length){
        buyerCompanies = buyerCompanies.map(company => {
            const companyName = company.companyName
            return { ...company, displayName: companyName }
        })
    }

    useEffect(() => {
        dispatch(fetchBuyerCompanies())

        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    useEffect(() => {
        if (['kamName', 'companyName'].includes(searchParams.searchBy)) {
            autoCompleteSearch(searchParams.searchBy, searchParams.searchText)
        }
    }, [searchParams.searchBy])

    const kamFuzzSearch = useFuzzSearch(pmCompanyUsers, "displayName")
    const companyFuzzSearch = useFuzzSearch(buyerCompanies, "displayName")

    const addFilterOption = (srNO) => {
        let relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if (relatedConfig.name === "branchId") {
            const updatedCompanyBranches = companyBranches.map(eachCompanyBranch => ({ ...eachCompanyBranch, label: eachCompanyBranch.branchName, value: eachCompanyBranch.branchId }))
            relatedConfig = {...relatedConfig, options: updatedCompanyBranches}
        }
        dispatch(updateSearchOptions([...searchOptions, { ...relatedConfig }]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "AUTO_COMPLETE") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value

        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }

        const updatedSearchParams = { ...searchParams }
        
        updatedSearchParams[event.target.name] = eventValue

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if (event.target.name === "searchText" && event.target.value.length > 3) {
            const availableOptions = [...searchConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            if(!isSearchByLocked){
                updatedSearchParams.searchBy = matchedPattern
            }
        }

        if (event.target.name === "searchBy"){
            dispatch(setIsSearchByLocked(true))
        }
        
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleSearchByLock = () => {
        if(isSearchByLocked && searchParams.searchText === ""){
            const updatedSearchParams = { ...searchParams, searchBy : "all" }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        else if(isSearchByLocked && searchParams.searchText !== ""){
            const availableOptions = [...searchConfig]
            const matchedPattern = getMatchedRegexPattern(searchParams.searchText, availableOptions, searchParams.searchBy)
            const updatedSearchParams = { ...searchParams, searchBy : matchedPattern }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        dispatch(setIsSearchByLocked(!isSearchByLocked))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const autoCompleteSearch = (searchType, query) => {
        const suggestions = getAutoSearchSuggestions({
            searchType,
            query,
            pmCompanyUsers,
            buyerCompanies,
            kamFuzzSearch,
            companyFuzzSearch
        })

        setSuggestions(suggestions)
    }
    
    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const isSearchByVisible = searchParams.searchText || isSearchByLocked

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSearch = () => {
        let errorFound = false

        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })
        
        const updatedSearchParams = { ...searchParams }

        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        const sellerPOVendorPaymentParams = {
            ...paginationParams,
            ...updatedSearchParams,
            pageNo: 0,
            paginator: true
        }

        if (!Object.keys(updatedSearchParams).some(label => docNoSearchLabels.includes(label))) {
            if (!searchParams.fromDate || !searchParams.toDate) {
                return dispatch(raiseToast({ severity: 'error', detail: "Please select from date and to date", life: 3000 }))
            }

            let from_date = searchParams.fromDate
            let to_date = searchParams.toDate
            if (searchParams.fromDate) {
                from_date = moment(searchParams.fromDate, "DD.MM.YYYY").toDate()
            }
            if (searchParams.toDate) {
                to_date = moment(searchParams.toDate, "DD.MM.YYYY").toDate()
            }

            const date_difference = moment(new Date(to_date)).diff(moment(new Date(from_date)), 'days')
            if (date_difference > 180) {
                return dispatch(raiseToast({ severity: 'error', detail: "Please select Date range within 6 month", life: 3000 }))
            }

            if (searchParams.dateType === "paymentDate") {
                sellerPOVendorPaymentParams.sortBy = "paymentDate"
            }
        } else {
            sellerPOVendorPaymentParams.dateType = null
        }

        if (searchParams.searchText && !searchParams.searchBy) {
            return dispatch(raiseToast({ severity: 'error', detail: "Please select search option!", life: 3000 }))
        }

        if (!errorFound) {
            dispatch(fetchSellerPOVendorPayments(sellerPOVendorPaymentParams))
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadVPDataForVendorPayment("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadVPDataForVendorPayment("CSV")
        }
    ]

    const downloadVPDataForVendorPayment = async (dataFormat) => {
        const updatedSearchParams = { ...searchParams }
        if(updatedSearchParams.searchBy !== "all"){
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        const vendorPaymentResponse = await searchSellerPOVendorPayments({ ...paginationParams, ...updatedSearchParams, paginator: false, limitedPagination: false })

        if (vendorPaymentResponse.failure) {
            dispatch(raiseToast({ severity: 'error', detail: vendorPaymentResponse.data.error }))
        } else {
            const vendorPaymentsNonCreditData = vendorPaymentResponse.data.data
            const vendorPayments = vendorPaymentResponse.data.vendorPayments

            const downloadData = []
            vendorPaymentsNonCreditData.map((eachVendorPayment, index) => {
                const createdDate = moment(eachVendorPayment.createdAt).format(DATE_FORMAT)
                const relatedExpenseCategory = expenseCategories.find(eachCategory => eachCategory.expenseCategoryId === eachVendorPayment.expenseCategoryId)

                const relatedVendorPayments = vendorPayments.filter(eachVP => eachVendorPayment.sellerPOId === eachVP.sellerPOId)

                let totalPaidAmount = 0
                let requestedPaymentExists = false
                relatedVendorPayments.map(eachVP => {
                    if (eachVP.statusId === 10) {
                        totalPaidAmount += eachVP.amount
                        if (eachVP.tdsAmount) {
                            totalPaidAmount += eachVP.tdsAmount
                        }
                    }
                    if (eachVP.statusId === 12) {
                        requestedPaymentExists = true
                    }
                })

                let amountDue = round(eachVendorPayment["totalPOAmount"] - totalPaidAmount)

                let vpStatus = ""

                if (eachVendorPayment.statusId === 4) {
                    vpStatus = "Closed"
                } else if (eachVendorPayment.statusId === 13) {
                    vpStatus = "VP Paused"
                } else if (relatedVendorPayments.length == 0) {
                    vpStatus = "Not Initiated"
                } else if (requestedPaymentExists) {
                    vpStatus = "Awaiting Approval"
                } else if (amountDue <= SELLERPO_VP_MIN_PRICE_DIFF) {
                    vpStatus = "Completed"
                } else {
                    vpStatus = "Pending"
                }

                const obj = {
                    "Sr No.": index + 1,
                    "Date": createdDate,
                    "Seller Company Name": eachVendorPayment["companies.companyName"],
                    "Buyer Company Name": eachVendorPayment["buyerPO.companies.companyName"],
                    "Document No.": eachVendorPayment["docNo"],
                    "KAM": eachVendorPayment["users.firstName"] + " " + eachVendorPayment["users.lastName"],
                    "Account Due": amountDue,
                    "Amount Paid": totalPaidAmount,
                    "Total Price": parseFloat(eachVendorPayment["totalPOAmount"]),
                    "Status": vpStatus,
                    "Expense Category": relatedExpenseCategory?.expenseCategoryName
                }
                downloadData.push(obj)
            })

            if (dataFormat === "CSV") {
                let csv = Papa.unparse(downloadData, { download: true })
                let blob = new Blob([csv])
                let a = window.document.createElement("a")
                a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
                a.download = "seller_po_data.csv"
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            }
            if (dataFormat === "XLSX") {
                const ws = utils.json_to_sheet(downloadData)

                const range = utils.decode_range(ws['!ref']);
                for (let R = range.s.r; R <= range.e.r; ++R) {
                    for (let C = range.s.c; C <= range.e.c; ++C) {
                        const cellAddress = utils.encode_cell({ r: R, c: C });
                        if (ws[cellAddress] && ws[cellAddress].t === 's' && ws[cellAddress].v.charAt(0) === "'") {
                            ws[cellAddress].v = ws[cellAddress].v.substring(1);
                        }
                    }
                }

                const wb = utils.book_new()
                utils.book_append_sheet(wb, ws, 'Sheet1')
                const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'seller_po_data.xlsx'
                a.click()
                URL.revokeObjectURL(url)
            }
        }
    }

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleSearchKeyPress}>
            <div className="grid">
                {searchOptions.map(eachOption => {
                    if (eachOption.type === "SEARCH_TEXT") {

                        let searchByOptions = [{ label: '', value: 'all' }]
                        searchConfig.map(eachSearchBy => {
                            if (eachSearchBy.isSearchByOption) {
                                const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                if(!optionInUse){
                                    searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                }
                            }
                        })

                        return (
                            <div className={`flex flex-column col-12 m-0 p-0 ${isSearchByVisible ? 'search-by-filter-set' : ''}`}>
                                <div className={`flex flex-column col-12`}>
                                    {['kamName', 'companyName'].includes(searchParams.searchBy) ? (
                                        <AutoCompleteField
                                            value={searchParams.searchText}
                                            suggestions={suggestions}
                                            searchMethod={(e) => autoCompleteSearch(searchParams.searchBy, e.query)}
                                            field="displayName"
                                            labelText="Search Text"
                                            onChange={handleSearchOnChange}
                                            name="searchText"
                                            dropdown
                                            refName={searchTextRef}
                                        />
                                    ) : (
                                        <TextInputField
                                            name="searchText"
                                            labelText="Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    )}
                                </div>
                                {(searchParams.searchText || isSearchByLocked)  && (
                                    <>
                                        <div className="flex justify-content-end p-0 col-12 remove-opt" style={{height: '0px'}}>
                                            <i
                                                className={`pi ${isSearchByLocked ? 'pi-lock' : 'pi-lock-open'}`}
                                                style={{ position: 'absolute', top: '-2px', left: '70px', cursor: 'pointer', color: 'orange', zIndex: '1000' }}
                                                onClick={() => handleSearchByLock()}
                                            ></i>
                                        </div>
                                        <div className="flex flex-column col-12" style={{marginTop: '-12px'}}>
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                isSearchByLocked={isSearchByLocked}
                                                handleSearchByLock={handleSearchByLock}
                                                isLockVisible={true}
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    }

                    if (eachOption.type === "DATE") {
                        return (
                            <>
                                <DateFilter 
                                    disabled={docNoSearchLabels.includes(searchParams.searchBy)}
                                    eachOption={eachOption} 
                                    searchParams={searchParams} 
                                    dateOptions={["MTD", "PM", "L15", "L30"]}
                                    handleDateRangeChange={handleDateRangeChange} 
                                    handleSearchOnChange={handleSearchOnChange}
                                    removeFilterOption={removeFilterOption}
                                />
                            </>
                        )
                    }

                    if (eachOption.type === "SELECT") {
                        const updatedOptions = eachOption.options.map(eachSelectOption => ({ label: eachSelectOption.label, value: eachSelectOption.value }))
                        return (
                            <>
                                {!eachOption.static ?
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                    :
                                    null
                                }
                                <div className="flex flex-column col-12">
                                    <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                </div>
                            </>
                        )
                    }

                    if (eachOption.type === "MULTISELECT") {
                        return (
                            <>
                                {!eachOption.static ?
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                    :
                                    null
                                }
                                {
                                    <div className="flex flex-column col-12">
                                        <MultiSelectField name={eachOption.name} options={eachOption.options} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                }
                            </>
                        )
                    }

                    if (eachOption.type === "TEXT") {
                        return (
                            <>
                                {!eachOption.static &&
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                }
                                {
                                    <div className="flex flex-column col-12">
                                        <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                }
                            </>
                        )
                    }

                    if (eachOption.type === "AUTO_COMPLETE") {
                        return (
                            <>
                                {!eachOption.static &&
                                    <div className="flex justify-content-end p-0 col-12 remove-opt">
                                        <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                    </div>
                                }
                                {
                                    <div className="flex flex-column col-12">
                                        <AutoCompleteField
                                            value={searchParams[eachOption.name]}
                                            suggestions={suggestions}
                                            searchMethod={(e) => autoCompleteSearch(eachOption.name, e.query)}
                                            field="displayName"
                                            labelText={eachOption.label}
                                            onChange={handleSearchOnChange}
                                            name={eachOption.name}
                                            dropdown
                                        />
                                    </div>
                                }
                            </>
                        )
                    }
                })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isTableLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isTableLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[243]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;